import React, { useState, useEffect } from 'react';
import { Heart, Calendar, MapPin, Users, Sparkles, LogOut, User, X, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import MatchingLoginModal from '../components/MatchingLoginModal';
import ProfileModal from '../components/ProfileModal';

interface HeartLinkUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  photo_url: string | null;
}

interface Event {
  id: string;
  name: string;
  date: string;
  time: string;
  location: string;
  city: string;
  age_range: string;
  matches_sent: boolean;
}

interface Participant {
  id: string;
  name: string;
  email: string;
  phone: string;
  photo_url: string | null;
  participant_number?: number;
}

interface Match {
  id: string;
  participant1_id: string;
  participant2_id: string;
  seen_by_user1: boolean | null;
  seen_by_user2: boolean | null;
  created_at: string;
  event_id: string;
  source: string;
  matched_user?: Participant;
}

const HeartLink = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<HeartLinkUser | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [interests, setInterests] = useState<Set<string>>(new Set());
  const [matches, setMatches] = useState<Match[]>([]);
  const [showMatches, setShowMatches] = useState(false);
  const [newMatchNotification, setNewMatchNotification] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [participantNumber, setParticipantNumber] = useState<number | null>(null);

  useEffect(() => {
    const validateAndRestoreSession = async () => {
      try {
        const storedUser = localStorage.getItem('heartlink_user');
        if (!storedUser) return;

        const user = JSON.parse(storedUser);

        const { data, error } = await supabase
          .from('contacts')
          .select('id, name, email, phone, photo_url')
          .eq('id', user.id)
          .maybeSingle();

        if (error || !data) {
          localStorage.removeItem('heartlink_user');
          return;
        }

        setCurrentUser(data);
        setShowLoginModal(false);
      } catch {
        localStorage.removeItem('heartlink_user');
      }
    };

    validateAndRestoreSession();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadEvents();
      loadParticipantNumber();
    }
  }, [currentUser]);

  const loadParticipantNumber = async () => {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('event_participants')
        .select('participant_number, event_id')
        .eq('participant_id', currentUser.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) return;

      if (data?.participant_number) {
        setParticipantNumber(data.participant_number);
      }
    } catch {
    }
  };

  useEffect(() => {
    if (currentUser && selectedEvent) {
      loadParticipants();
      loadInterests();
      loadMatches();

    }
  }, [currentUser, selectedEvent]);

  const loadEvents = async () => {
    if (!currentUser) return;

    try {
      const { data: participantData, error: participantError } = await supabase
        .from('event_participants')
        .select(`
          event:events (
            id,
            name,
            date,
            time,
            location,
            city,
            age_range,
            status,
            matches_sent
          )
        `)
        .eq('participant_id', currentUser.id);

      if (participantError) return;

      const userEvents = participantData
        ?.map((p: any) => p.event)
        .filter((event: any) => event && event.status !== null) || [];

      setEvents(userEvents);

      if (userEvents.length === 1) {
        setSelectedEvent(userEvents[0]);
      }
    } catch {
    }
  };

  const loadParticipants = async () => {
    if (!selectedEvent) return;

    try {
      const { data: eventParticipants, error: participantsError } = await supabase
        .from('event_participants')
        .select('participant_id, participant_number')
        .eq('event_id', selectedEvent.id);

      if (participantsError || !eventParticipants || eventParticipants.length === 0) {
        setParticipants([]);
        return;
      }

      const participantIds = eventParticipants
        .map(ep => ep.participant_id)
        .filter(id => id !== currentUser?.id);

      if (participantIds.length === 0) {
        setParticipants([]);
        return;
      }

      const { data: contacts } = await supabase
        .from('contacts')
        .select('id, name, email, phone, photo_url')
        .in('id', participantIds);

      const participantsWithDetails: Participant[] = (contacts || []).map(contact => {
        const eventParticipant = eventParticipants.find(ep => ep.participant_id === contact.id);
        const firstName = contact.name?.split(' ')[0] || contact.email?.split('@')[0] || '?';

        return {
          id: contact.id,
          name: firstName,
          email: contact.email || '',
          phone: contact.phone || '',
          photo_url: contact.photo_url || null,
          participant_number: eventParticipant?.participant_number,
        };
      });

      setParticipants(participantsWithDetails);
    } catch {
    }
  };

  const loadInterests = async () => {
    if (!selectedEvent || !currentUser) return;

    try {
      const { data, error } = await supabase
        .from('user_interests')
        .select('to_user_id')
        .eq('event_id', selectedEvent.id)
        .eq('from_user_id', currentUser.id);

      if (error) return;

      setInterests(new Set(data?.map(i => i.to_user_id) || []));
    } catch {
    }
  };

  const loadMatches = async () => {
    if (!selectedEvent || !currentUser) return;

    try {
      const { data: allMatches, error: matchesError } = await supabase
        .from('matches')
        .select('*')
        .eq('event_id', selectedEvent.id)
        .eq('source', 'heartlink')
        .or(`participant1_id.eq.${currentUser.id},participant2_id.eq.${currentUser.id}`);

      if (matchesError) return;

      if (!allMatches || allMatches.length === 0) {
        setMatches([]);
        return;
      }

      const otherContactIds = allMatches.map(m =>
        m.participant1_id === currentUser.id ? m.participant2_id : m.participant1_id
      );

      const { data: matchedContacts } = await supabase
        .from('contacts')
        .select('id, name, email, phone, photo_url')
        .in('id', otherContactIds);

      const contactsMap = new Map(
        (matchedContacts || []).map(c => [c.id, c])
      );

      const matchesWithUsers = allMatches.map(match => {
        const otherId = match.participant1_id === currentUser.id
          ? match.participant2_id
          : match.participant1_id;
        const contact = contactsMap.get(otherId);
        if (!contact) return null;

        return {
          ...match,
          matched_user: {
            id: contact.id,
            name: contact.name?.split(' ')[0] || contact.email?.split('@')[0] || '?',
            email: contact.email,
            phone: contact.phone,
            photo_url: contact.photo_url,
          }
        };
      });

      setMatches(matchesWithUsers.filter(m => m !== null));
    } catch {
    }
  };

  const handleSelectEvent = (event: Event) => {
    setSelectedEvent(event);
  };

  const toggleInterest = async (participantId: string) => {
    if (!selectedEvent || !currentUser || selectedEvent.matches_sent) return;

    try {
      if (interests.has(participantId)) {
        const { error } = await supabase
          .from('user_interests')
          .delete()
          .eq('event_id', selectedEvent.id)
          .eq('from_user_id', currentUser.id)
          .eq('to_user_id', participantId);

        if (error) return;

        setInterests(prev => {
          const newSet = new Set(prev);
          newSet.delete(participantId);
          return newSet;
        });
      } else {
        const { error } = await supabase
          .from('user_interests')
          .insert({
            event_id: selectedEvent.id,
            from_user_id: currentUser.id,
            to_user_id: participantId
          });

        if (error) return;

        setInterests(prev => new Set(prev).add(participantId));
      }

      await loadMatches();
    } catch {
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('heartlink_user');
    setCurrentUser(null);
    setSelectedEvent(null);
    setParticipants([]);
    setInterests(new Set());
    setMatches([]);
    setEvents([]);
    navigate('/');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-red-50">
      {newMatchNotification && (
        <div className="fixed top-4 right-4 z-50 bg-gradient-to-r from-pink-500 to-rose-600 text-white px-6 py-4 rounded-lg shadow-lg animate-bounce">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6" />
            <span className="font-semibold">Nuovo Match!</span>
          </div>
        </div>
      )}

      <header className="bg-gradient-to-r from-pink-500 via-rose-500 to-red-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-white rounded-lg w-12 h-12 flex items-center justify-center shadow-lg">
                <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br from-pink-500 to-rose-600">
                  HL
                </span>
              </div>
            </div>
            {currentUser && (
              <div className="flex items-center gap-3">
                {participantNumber !== null && (
                  <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/40">
                    <span className="text-white font-bold text-lg">#{participantNumber}</span>
                  </div>
                )}
                <button
                  onClick={() => setShowProfile(true)}
                  className="bg-white/20 hover:bg-white/30 text-white p-2.5 rounded-lg font-medium transition-all border border-white/40"
                  title="Profilo"
                >
                  <User className="h-5 w-5" />
                </button>
                {selectedEvent?.matches_sent && (
                  <button
                    onClick={() => setShowMatches(true)}
                    className="relative bg-white/20 hover:bg-white/30 text-white p-2.5 rounded-lg font-medium transition-all border border-white/40"
                    title="I Miei Match"
                  >
                    <Heart className="h-5 w-5" />
                    {matches.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                        {matches.length}
                      </span>
                    )}
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="bg-white/20 hover:bg-white/30 text-white p-2.5 rounded-lg font-medium transition-all border border-white/40"
                  title="Esci"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!selectedEvent ? (
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              I Tuoi Eventi
            </h2>
            {events.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map(event => (
                  <div
                    key={event.id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
                  >
                    <div className="bg-gradient-to-r from-pink-500 to-rose-600 p-4 text-white">
                      <h3 className="text-xl font-bold">{event.name}</h3>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center gap-3 text-gray-700">
                          <Calendar className="h-5 w-5 text-pink-500" />
                          <span className="text-sm">{formatDate(event.date)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-700">
                          <MapPin className="h-5 w-5 text-pink-500" />
                          <span className="text-sm">{event.location}, {event.city}</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-700">
                          <Users className="h-5 w-5 text-pink-500" />
                          <span className="text-sm">Età: {event.age_range}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleSelectEvent(event)}
                        className="w-full bg-gradient-to-r from-pink-500 to-rose-600 text-white py-2 rounded-lg font-semibold hover:from-pink-600 hover:to-rose-700 transition-all"
                      >
                        Vedi Partecipanti
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 text-lg font-semibold mb-2">
                  Non sei ancora iscritto a nessun evento
                </p>
                <p className="text-gray-500 text-sm">
                  Contatta l'organizzatore per essere aggiunto a un evento
                </p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{selectedEvent.name}</h2>
                <p className="text-gray-600">{formatDate(selectedEvent.date)} - {selectedEvent.location}</p>
              </div>
              <button
                onClick={() => {
                  setSelectedEvent(null);
                  setParticipants([]);
                  setInterests(new Set());
                }}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-all"
              >
                Cambia Evento
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...participants].sort((a, b) => (a.participant_number ?? Infinity) - (b.participant_number ?? Infinity)).map(participant => {
                const isInterested = interests.has(participant.id);
                return (
                  <div
                    key={participant.id}
                    className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all relative ${
                      isInterested ? 'ring-4 ring-pink-500' : ''
                    }`}
                  >
                    <div className="p-6">
                      <div className="flex flex-col items-center">
                        {participant.participant_number !== undefined && (
                          <div className="absolute top-3 left-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold text-sm px-3 py-1 rounded-full shadow-lg">
                            #{participant.participant_number}
                          </div>
                        )}
                        {participant.photo_url ? (
                          <img
                            src={participant.photo_url}
                            alt={participant.name || participant.email}
                            className="w-32 h-32 rounded-full object-cover border-4 border-pink-200 mb-4"
                          />
                        ) : (
                          <div className="w-32 h-32 rounded-full flex items-center justify-center text-white text-4xl font-bold border-4 mb-4 bg-gradient-to-br from-pink-400 to-rose-500 border-pink-200">
                            {getInitials(participant.name || participant.email)}
                          </div>
                        )}
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {participant.name || participant.email.split('@')[0]}
                        </h3>
                        <button
                          onClick={() => toggleInterest(participant.id)}
                          disabled={selectedEvent.matches_sent}
                          className={`w-full py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                            selectedEvent.matches_sent
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : isInterested
                                ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white hover:from-pink-600 hover:to-rose-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <Heart className={`h-5 w-5 ${!selectedEvent.matches_sent && isInterested ? 'fill-white' : ''}`} />
                          {selectedEvent.matches_sent ? 'Preferenze chiuse' : isInterested ? 'Interessato' : 'Segna Interesse'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {participants.length === 0 && (
              <div className="text-center py-12">
                <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 text-lg">Nessun partecipante ancora</p>
              </div>
            )}
          </div>
        )}
      </main>

      {showMatches && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-rose-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-8 w-8" />
                  <h2 className="text-2xl font-bold">I Tuoi Match</h2>
                </div>
                <button
                  onClick={() => setShowMatches(false)}
                  className="hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {matches.length > 0 ? (
                <div className="space-y-4">
                  {matches.map(match => (
                    <div
                      key={match.id}
                      className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-4 border-2 border-pink-200"
                    >
                      <div className="flex items-center gap-4">
                        {match.matched_user?.photo_url ? (
                          <img
                            src={match.matched_user.photo_url}
                            alt="Match"
                            className="w-16 h-16 rounded-full object-cover border-2 border-pink-300"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-2xl font-bold border-2 border-pink-300">
                            {match.matched_user?.name ? match.matched_user.name.split(' ')[0].charAt(0).toUpperCase() : '?'}
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900">
                            {match.matched_user?.name ? match.matched_user.name.split(' ')[0] : 'Utente'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Match creato {new Date(match.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                        <div className="text-pink-500">
                          <Heart className="h-8 w-8 fill-pink-500" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Heart className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600 text-lg">Nessun match ancora</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Continua a segnare interesse per trovare il tuo match perfetto!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <MatchingLoginModal
        isOpen={showLoginModal}
        onClose={() => navigate('/')}
        onLoginSuccess={(user) => {
          setCurrentUser(user);
          setShowLoginModal(false);
        }}
      />

      {currentUser && (
        <ProfileModal
          isOpen={showProfile}
          onClose={() => setShowProfile(false)}
          user={currentUser}
          participantNumber={participantNumber}
          onPhotoUpdate={(photoUrl) => {
            setCurrentUser({ ...currentUser, photo_url: photoUrl });
            const storedUser = localStorage.getItem('heartlink_user');
            if (storedUser) {
              const user = JSON.parse(storedUser);
              localStorage.setItem('heartlink_user', JSON.stringify({ ...user, photo_url: photoUrl }));
            }
          }}
        />
      )}
    </div>
  );
};

export default HeartLink;
